import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';
import { useScope, brandColorVar } from '@/context/ScopeContext';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import TESTIDS from '@/constants/testIds';

export default function Drafts() {
    const { brandId, brands, accounts } = useScope();
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const params = {};
        if (brandId) params.brand_id = brandId;
        try {
            const r = await apiClient.get('/content/drafts', { params });
            setDrafts(r.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [brandId]);

    const remove = async (id) => {
        try {
            await apiClient.delete(`/content/drafts/${id}`);
            toast.success('Draft deleted');
            load();
        } catch (e) {
            toast.error('Failed to delete');
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Drafts</h1>
                <p className="text-sm text-muted-foreground">All saved drafts. Filter by scope using the top bar.</p>
            </div>
            {loading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
            ) : drafts.length === 0 ? (
                <Card className="border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
                    No drafts saved yet. Use the Composer to generate and save one.
                </Card>
            ) : (
                <Card className="border-border bg-card">
                    <ul className="divide-y divide-border">
                        {drafts.map((d) => {
                            const brand = brands.find((b) => b.id === d.brand_id);
                            const accent = brandColorVar(brand?.color_slot);
                            const destinationHandles = (d.destination_account_ids || [])
                                .map((id) => accounts.find((a) => a.id === id)?.handle)
                                .filter(Boolean);
                            const headline = d.edited_caption || (d.output?.captions?.[0]?.long ?? d.output?.captions?.[0]?.short ?? '');
                            return (
                                <li
                                    key={d.id}
                                    className="grid grid-cols-1 items-start gap-3 px-4 py-3 md:grid-cols-[1fr_140px_140px_60px]"
                                    style={{ '--brand-accent-current': accent }}
                                    data-testid={TESTIDS.draftRow}
                                >
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="inline-block h-2 w-2 shrink-0 rounded-full"
                                                style={{ backgroundColor: `hsl(${accent})` }}
                                            />
                                            <span className="font-mono text-[12px] text-muted-foreground">{brand?.name || '—'}</span>
                                            <span className="text-[11px] text-muted-foreground">· {d.content_type}</span>
                                        </div>
                                        <p className="mt-1 line-clamp-2 text-sm">{headline || <span className="text-muted-foreground">(no caption)</span>}</p>
                                        <div className="mt-1 flex flex-wrap gap-1">
                                            {destinationHandles.slice(0, 4).map((h) => (
                                                <span key={h} className="font-mono text-[11px] text-muted-foreground">@{h}</span>
                                            ))}
                                            {destinationHandles.length > 4 ? (
                                                <span className="text-[11px] text-muted-foreground">+{destinationHandles.length - 4}</span>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="text-[12px] text-muted-foreground">
                                        <div className="font-mono">{new Date(d.created_at).toLocaleString()}</div>
                                    </div>
                                    <div>
                                        <StatusBadge status={d.status} />
                                    </div>
                                    <div className="text-right">
                                        <Button size="sm" variant="ghost" onClick={() => remove(d.id)} className="text-muted-foreground hover:text-foreground">
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </Card>
            )}
        </div>
    );
}
